import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShoppingBag, CheckCircle2, Package, Receipt, ExternalLink, Sparkles } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; }
};

export default function StoreTab() {
  const [prods, setProds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lastOrder, setLastOrder] = useState(null);

  const loadProducts = () => api.get("/store/products").then(r => setProds(r.data.products));
  const loadOrders = () => api.get("/store/orders").then(r => setOrders(r.data.orders)).catch(()=>{});

  useEffect(() => { loadProducts(); loadOrders(); }, []);

  const buy = async (id) => {
    try {
      const res = await api.post("/store/orders", { product_id: id, qty: 1 });
      const order = res.data.order;
      setLastOrder(order);
      toast.success(`Order placed! ${order.product_name} · $${order.total.toFixed(2)}`);
      loadOrders();
      // Auto-clear the receipt banner after 12 sec so the user has time to see it
      setTimeout(() => setLastOrder(null), 12000);
    } catch (e) { toast.error("Could not place order"); }
  };

  return (
    <div className="space-y-12" data-testid="store-tab">
      {/* SECTION 1: Recent Orders (top) */}
      <section className="rounded-2xl p-7 md:p-9" style={{background:'linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 100%)', border:'2px solid #F39A1F'}}>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-red-wine flex items-center justify-center flex-shrink-0">
              <Receipt className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[11px] tracking-wider-cap text-gold-deep font-bold">Step 2 · Track</div>
              <h2 className="text-3xl uppercase text-red-wine leading-tight">Your Orders</h2>
              <div className="text-sm text-foreground mt-0.5">Every order shows up here automatically</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-serif-display text-red-wine leading-none">{orders.length}</div>
            <div className="text-[10px] tracking-wider-cap text-foreground mt-1">order{orders.length===1?'':'s'} placed</div>
          </div>
        </div>

        {/* Receipt for the most recent order */}
        {lastOrder && (
          <div className="mb-4 p-5 rounded-xl bg-white border-2" style={{borderColor:'#F39A1F'}} data-testid="last-order-banner">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-gold-deep"/>
              <div className="text-[11px] font-bold uppercase tracking-wider-cap text-gold-deep">Order Confirmed!</div>
            </div>
            <div className="text-xl uppercase text-red-wine">{lastOrder.product_name}</div>
            <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
              <div><div className="text-[10px] tracking-wider-cap text-foreground">Qty</div><div className="font-bold">{lastOrder.qty}</div></div>
              <div><div className="text-[10px] tracking-wider-cap text-foreground">Total</div><div className="font-bold">${lastOrder.total.toFixed(2)}</div></div>
              <div><div className="text-[10px] tracking-wider-cap text-foreground">Status</div><div className="font-bold capitalize">{lastOrder.status}</div></div>
            </div>
          </div>
        )}

        {orders.length === 0 ? (
          <div className="bg-white/60 rounded-xl p-8 text-center border-2 border-dashed border-[#F39A1F]/50">
            <Package className="w-10 h-10 text-gold-deep mx-auto mb-3" strokeWidth={1.5}/>
            <div className="text-xl uppercase text-red-wine mb-2">No orders yet</div>
            <p className="text-sm text-foreground">Scroll down and click <strong>ORDER</strong> on any product to place your first one.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {orders.slice(0,10).map(o => (
              <div key={o.id} className="bg-white rounded-lg p-4 flex items-center justify-between flex-wrap gap-2" style={{border:'1px solid rgba(201,162,39,0.3)'}} data-testid={`order-${o.id}`}>
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-gold-deep"/>
                  <div>
                    <div className="font-bold text-foreground">{o.product_name}</div>
                    <div className="text-[11px] tracking-wider-cap text-foreground">Qty: {o.qty} · {fmtDate(o.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 bg-white text-gold-deep border border-[#F39A1F]/50 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider-cap">{o.status}</span>
                  <div className="font-serif-display text-xl text-red-wine">${o.total.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Divider */}
      <div className="flex items-center gap-4 px-6">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#F39A1F] to-transparent"></div>
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold-deep font-bold">— · —</div>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#F39A1F] to-transparent"></div>
      </div>

      {/* SECTION 2: Browse Products */}
      <section className="rounded-2xl p-7 md:p-9 bg-white" style={{border:'2px solid rgba(201,162,39,0.35)'}}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-gold-deep flex items-center justify-center flex-shrink-0" style={{backgroundColor:'#F39A1F'}}>
            <ShoppingBag className="w-6 h-6 text-[#0F172A]"/>
          </div>
          <div>
            <div className="text-[11px] tracking-wider-cap text-gold-deep font-bold">Step 1 · Shop</div>
            <h2 className="text-3xl uppercase text-red-wine leading-tight">Christian Merchandise</h2>
            <div className="text-sm text-foreground mt-0.5">Christian tees · mugs · books · Click ORDER to buy. <span className="text-foreground/60">Looking for supplements? <a href="/dashboard/goli" className="text-red-wine font-bold underline">Visit our Supplements page →</a></span></div>
          </div>
        </div>

        {/* Merchandise grid (supplements live on a dedicated /dashboard/goli page) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {prods.filter(p => p.category !== "supplement").map(p => {
            const hasExternal = !!p.external_url;
            return (
              <div key={p.id} className="bg-white rounded-xl overflow-hidden shadow-sm border border-border/60 hover:shadow-md transition-shadow flex flex-col" data-testid={`product-${p.id}`}>
                <img src={p.image} className="w-full h-44 object-contain p-3 bg-white" alt="" />
                <div className="p-5 flex flex-col flex-1">
                  <div className="text-[10px] tracking-wider-cap text-gold-deep font-bold">{p.brand} · {p.category}</div>
                  <div className="text-xl text-red-wine mt-1 uppercase">{p.name}</div>
                  <p className="text-sm text-foreground mt-1 flex-1">{p.description}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="font-serif-display text-2xl text-red-wine">{p.price === 0 ? "Free" : `$${p.price.toFixed(2)}`}</div>
                    {hasExternal ? (
                      <a
                        href={p.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`buy-${p.id}`}
                        className="btn-gold !py-2 !px-4 !text-[11px] inline-flex items-center gap-1.5"
                      >
                        Buy Now <ExternalLink className="w-3 h-3"/>
                      </a>
                    ) : (
                      <button onClick={() => buy(p.id)} data-testid={`buy-${p.id}`} className="btn-gold !py-2 !px-4 !text-[11px]">{p.price === 0 ? "Get Now" : "Order Now"}</button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
